// Invite page (§6.2). Public teaser via get_invite_teaser; writes the slug to
// localStorage as pendingInvite before auth; same single Discord button.

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Page } from "../components/Layout.tsx";
import { DevLogins } from "../components/DevLogins.tsx";
import { AuthButtons } from "../components/AuthButtons.tsx";
import { useAuth, setPendingInvite } from "../auth/AuthProvider.tsx";
import { getInviteTeaser, createBlend, getMyProfile, type InviteTeaser } from "../lib/api.ts";

export default function InvitePage() {
  const { shareSlug = "" } = useParams();
  const auth = useAuth();
  const navigate = useNavigate();
  const [teaser, setTeaser] = useState<InviteTeaser | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPendingInvite(shareSlug);
    getInviteTeaser(shareSlug)
      .then((t) => (t ? setTeaser(t) : setNotFound(true)))
      .catch(() => setNotFound(true));
  }, [shareSlug]);

  // If already authed + profiled, resolve the blend immediately (§6.3).
  useEffect(() => {
    if (auth.loading || !auth.user) return;
    getMyProfile().then(async (p) => {
      if (!p) {
        navigate("/onboarding");
        return;
      }
      const res = await createBlend(shareSlug);
      if (res.self) {
        navigate("/me?toast=self-invite");
      } else if (res.blend_slug) {
        navigate(`/b/${res.blend_slug}`, { replace: true });
      }
    });
  }, [auth.loading, auth.user, shareSlug, navigate]);

  if (notFound) {
    return (
      <Page testid="screen-invite">
        <div className="card text-center">
          <h1 className="text-2xl font-semibold text-ink-900">Dead link</h1>
          <p className="mt-2 text-ink-400">This invite doesn't exist anymore.</p>
          <a href="/" className="btn-primary mt-4">Start fresh</a>
        </div>
      </Page>
    );
  }

  return (
    <Page testid="screen-invite">
      <div className="card text-center">
        <p className="text-sm uppercase tracking-wide text-ink-400">You've been invited</p>
        <h1 className="mt-2 text-2xl font-semibold text-ink-900" data-testid="invite-teaser">
          {teaser ? (
            <>
              <span className="text-gold-500">{teaser.display_name}</span> wants to blend film taste with you
            </>
          ) : (
            "Someone wants to blend film taste with you"
          )}
        </h1>
        {teaser && (
          <p className="mt-2 text-ink-400">
            {teaser.counts.watched.toLocaleString()} films · watchlist of {teaser.counts.watchlist.toLocaleString()}
          </p>
        )}
        <div className="mx-auto mt-6 flex max-w-sm flex-col gap-3">
          <AuthButtons discordLabel="Continue with Discord to blend" />
          <DevLogins />
        </div>
      </div>
    </Page>
  );
}
